import { useEffect, useRef, useState } from 'react';
import { getDashboardFeed } from '../api';
import DirectionBadge from './DirectionBadge';

function timeAgo(ts) {
  const diff = Date.now() - new Date(ts).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function truncateWords(text, maxWords = 30) {
  if (!text) return '';
  const serverTruncated = text.length >= 195;
  let words = text.trim().split(/\s+/);
  if (serverTruncated) words = words.slice(0, -1); // drop potentially partial last word
  if (words.length <= maxWords && !serverTruncated) return text;
  return words.slice(0, maxWords).join(' ') + '...';
}

const DIR_BORDER = {
  bullish: 'border-l-green-500',
  bearish: 'border-l-red-500',
  neutral: 'border-l-slate-600',
  mixed:   'border-l-yellow-500',
};

const ASSET_CLS = {
  bullish: 'bg-green-500/15 text-green-600',
  bearish: 'bg-red-500/15 text-red-600',
  neutral: 'bg-slate-500/15 text-slate-600',
};

function FeedItem({ item }) {
  const dir = item.article_direction?.toLowerCase() || 'neutral';

  return (
    <div className={`px-4 py-3 border-b border-slate-200 border-l-2 ${DIR_BORDER[dir] || DIR_BORDER.neutral} hover:bg-slate-100 transition-colors`}>
      <div className="flex justify-between items-center mb-1">
        <span className="text-[10px] font-bold text-primary uppercase tracking-wider">
          {item.source_name || 'Unknown'}
        </span>
        <span className="text-[10px] text-slate-500">{timeAgo(item.analysed_at)}</span>
      </div>

      <p className="text-xs text-slate-700 leading-relaxed mb-2">
        {truncateWords(item.headline)}
      </p>

      <div className="flex items-center gap-1.5 flex-wrap">
        {item.assets?.map(a => {
          const cls = ASSET_CLS[a.direction?.toLowerCase()] || ASSET_CLS.neutral;
          return (
            <span
              key={a.symbol}
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${cls}`}
              title={`${a.symbol}: ${a.score > 0 ? '+' : ''}${a.score?.toFixed(2)}`}
            >
              {a.symbol}
              <span className="font-normal opacity-70">
                {a.score > 0 ? '+' : ''}{a.score?.toFixed(2)}
              </span>
            </span>
          );
        })}
        <div className="ml-auto flex items-center gap-2">
          <DirectionBadge direction={item.article_direction} size="sm" />
          {item.source_url && (
            <a href={item.source_url} target="_blank" rel="noopener noreferrer"
               className="text-xs text-slate-600 hover:text-primary transition-colors">
              ↗
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

const SORT_OPTIONS = [
  { value: 'newest',  label: 'Newest' },
  { value: 'oldest',  label: 'Oldest' },
  { value: 'bullish', label: 'Bullish' },
  { value: 'bearish', label: 'Bearish' },
  { value: 'neutral', label: 'Neutral' },
  { value: 'mixed',   label: 'Mixed' },
];

const DIRECTION_FILTERS = ['bullish', 'bearish', 'neutral', 'mixed'];

function sortAndFilter(items, sort, sourceFilter) {
  let filtered = sourceFilter ? items.filter(i => i.source_name === sourceFilter) : items;
  if (DIRECTION_FILTERS.includes(sort)) {
    filtered = filtered.filter(i => (i.article_direction?.toLowerCase() || 'neutral') === sort);
  }
  if (sort === 'oldest') {
    return [...filtered].sort((a, b) => new Date(a.analysed_at) - new Date(b.analysed_at));
  }
  return [...filtered].sort((a, b) => new Date(b.analysed_at) - new Date(a.analysed_at));
}

const ALERT_THRESHOLD = -0.5;
const ALERT_FLAG_THRESHOLD = -0.6;

function extractAlerts(feed) {
  const alerts = [];
  for (const item of feed) {
    for (const a of (item.assets || [])) {
      if (typeof a.score === 'number' && a.score < ALERT_THRESHOLD) {
        alerts.push({
          symbol: a.symbol,
          score: a.score,
          alert: a.score < ALERT_FLAG_THRESHOLD,
          headline: item.headline,
          source: item.source_name,
          timestamp: item.analysed_at,
          article_id: item.article_id,
          source_url: item.source_url,
        });
      }
    }
  }
  return alerts;
}

export default function NewsFeed({ onNewArticle, onAlerts }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState('newest');
  const [sourceFilter, setSourceFilter] = useState('');
  const lastCountRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await getDashboardFeed(50);
        if (cancelled) return;
        const feed = data.feed || [];
        if (feed.length > lastCountRef.current) onNewArticle?.();
        lastCountRef.current = feed.length;
        setItems(feed);
        onAlerts?.(extractAlerts(feed));
      } catch {}
      setLoading(false);
    }
    load();
    const iv = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [onNewArticle, onAlerts]);

  const sources = [...new Set(items.map(i => i.source_name).filter(Boolean))].sort();
  const displayed = sortAndFilter(items, sort, sourceFilter);

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm flex flex-col h-full overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-300 flex-shrink-0">
        <h3 className="font-semibold text-primary text-sm">Live News Feed</h3>
        <span className="text-xs text-slate-500">{displayed.length} articles</span>
      </div>

      <div className="flex items-center gap-1.5 px-4 py-2 border-b border-slate-200 flex-shrink-0 flex-wrap">
        {SORT_OPTIONS.map(opt => (
          <button
            key={opt.value}
            onClick={() => setSort(opt.value)}
            className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-colors ${
              sort === opt.value
                ? 'bg-secondaryLight text-white'
                : 'bg-slate-200 text-slate-600 hover:bg-slate-300 hover:text-slate-800'
            }`}
          >
            {opt.label}
          </button>
        ))}
        <select
          value={sourceFilter}
          onChange={e => setSourceFilter(e.target.value)}
          className="ml-auto px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-200 text-slate-600 border border-slate-300 hover:bg-slate-300 hover:text-slate-800 transition-colors cursor-pointer"
        >
          <option value="">All Sources</option>
          {sources.map(src => (
            <option key={src} value={src}>{src}</option>
          ))}
        </select>
      </div>

      {loading && items.length === 0 && (
        <div className="flex flex-col items-center gap-3 p-10 text-slate-500 text-xs text-center">
          <div className="w-4 h-4 rounded-full border-2 border-slate-300 border-t-blue-600 animate-spin" />
          News poller is running — first articles will appear within 5 minutes
        </div>
      )}
      {!loading && displayed.length === 0 && (
        <p className="p-10 text-slate-500 text-xs text-center">
          {items.length === 0 ? 'No articles yet. Submit one manually or wait for the poller.' : 'No articles match this filter.'}
        </p>
      )}

      <div className="overflow-y-auto flex-1">
        {displayed.map(item => <FeedItem key={item.article_id} item={item} />)}
      </div>
    </div>
  );
}
